import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';

const CartContext = createContext();

// Cart reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_CART':
      return {
        ...state,
        ...action.payload
      };
    case 'ADD_TO_CART':
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + (action.payload.quantity || 1) }
              : item
          )
        };
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, quantity: action.payload.quantity || 1 }]
      };

    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: Math.max(1, action.payload.quantity) }
            : item
        )
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };

    case 'ADD_TO_WISHLIST':
      return {
        ...state,
        wishlist: [...state.wishlist, action.payload]
      };

    case 'REMOVE_FROM_WISHLIST':
      return {
        ...state,
        wishlist: state.wishlist.filter(item => item.id !== action.payload)
      };

    case 'APPLY_COUPON':
      return {
        ...state,
        appliedCoupon: action.payload
      };

    case 'REMOVE_COUPON':
      return {
        ...state,
        appliedCoupon: null
      };

    default:
      return state;
  }
};

// Cart provider component
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    wishlist: [],
    appliedCoupon: null
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        dispatch({ type: 'LOAD_CART', payload: parsedCart });
      } catch (error) {
        console.error('Error parsing saved cart:', error);
        localStorage.removeItem('cart');
      }
    }
    setIsInitialized(true);
  }, []);

  // Save cart to localStorage whenever it changes (but not during initial load)
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('cart', JSON.stringify(state));
    }
  }, [state, isInitialized]);

  // Cart actions
  const addToCart = (product, quantity = 1) => {
    dispatch({ type: 'ADD_TO_CART', payload: { ...product, quantity } });
  };

  const removeFromCart = (productId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const addToWishlist = (product) => {
    dispatch({ type: 'ADD_TO_WISHLIST', payload: product });
  };

  const removeFromWishlist = (productId) => {
    dispatch({ type: 'REMOVE_FROM_WISHLIST', payload: productId });
  };

  const applyCoupon = (coupon) => {
    dispatch({ type: 'APPLY_COUPON', payload: coupon });
  };

  const removeCoupon = () => {
    dispatch({ type: 'REMOVE_COUPON' });
  };

  // Calculate totals
  const getSubtotal = () => {
    return state.items.reduce((total, item) => {
      // Use calculated price if available, otherwise fall back to original calculation
      const price = item.calculatedPrice || parseFloat(item.regular_license_price || item.price || 0);
      return total + (price * item.quantity);
    }, 0);
  };

  const getDiscount = () => {
    if (!state.appliedCoupon) return 0;

    // Compute eligible subtotal: exclude items where full-payment coupons shouldn't apply
    // Only apply this exclusion for full payment coupons
    const isFullPaymentCoupon = state.appliedCoupon.type === 'full_payment';
    const eligibleSubtotal = state.items.reduce((total, item) => {
      const isIneligible = isFullPaymentCoupon && Boolean(item.disable_full_payment_coupon);
      if (isIneligible) return total; // skip this item for coupon discount
      const price = item.calculatedPrice || parseFloat(item.regular_license_price || item.price || 0);
      return total + (price * item.quantity);
    }, 0);

    if (eligibleSubtotal <= 0) return 0;

    // Handle both percentage and fixed amount discounts against eligible subtotal only
    if (state.appliedCoupon.discount_amount && state.appliedCoupon.discount_amount > 0) {
      return Math.min(state.appliedCoupon.discount_amount, eligibleSubtotal);
    }

    if (state.appliedCoupon.discount_percentage && state.appliedCoupon.discount_percentage > 0) {
      return (eligibleSubtotal * state.appliedCoupon.discount_percentage) / 100;
    }

    return 0;
  };

  const getTotal = () => {
    return getSubtotal() - getDiscount();
  };

  const getCartCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  const isInWishlist = (productId) => {
    return state.wishlist.some(item => item.id === productId);
  };

  const value = {
    ...state,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    addToWishlist,
    removeFromWishlist,
    applyCoupon,
    removeCoupon,
    getSubtotal,
    getDiscount,
    getTotal,
    getCartCount,
    isInWishlist
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 